import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type PaginationType =
  | 'page'
  | 'first'
  | 'last'
  | 'next'
  | 'previous'
  | 'start-ellipsis'
  | 'end-ellipsis';
export const DEFAULT_CARD_ITEMS_PER_PAGE = 12;
export const DEFAULT_TABLE_ITEMS_PER_PAGE = 10;

export type Paginator<T> = {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageCount: number;
  pagedItems: T[];
  handlePageChange: (page: number) => void;
  getPaginationAriaLabel: (
    type: PaginationType,
    page: number | null,
    selected: boolean,
  ) => string;
};

const useGetPaginationAriaLabel = (): ((
  type: PaginationType,
  page: number | null,
  selected: boolean,
) => string) => {
  const { t } = useTranslation();

  return (type: PaginationType, page: number | null, selected: boolean) => {
    if (type === 'page') {
      return t(
        selected
          ? 'ui.event-management.pagination.selected-page'
          : 'ui.event-management.pagination.unselected-page',
        { page },
      );
    }
    if (type === 'next') {
      return t('ui.event-management.pagination.next-page');
    }
    return '';
  };
};

const usePaginationV2 = <T>(
  items: T[],
  itemsPerPage = DEFAULT_CARD_ITEMS_PER_PAGE,
  totalAvailable?: number,
): Paginator<T> => {
  const getPaginationAriaLabel = useGetPaginationAriaLabel();
  const [pageNumber, setPageNumber] = useState(1);

  const totalCount = totalAvailable ?? items.length;
  const pageCount = Math.ceil(items.length / itemsPerPage);
  const firstItem = (pageNumber - 1) * itemsPerPage;
  const lastItem = firstItem + itemsPerPage;
  const pagedItems = items.slice(firstItem, lastItem);

  useEffect(() => {
    if (pageCount === 0) {
      setPageNumber(0);
      return;
    }
    if (pageNumber < 1) {
      setPageNumber(1);
    } else if (pageNumber > pageCount) {
      setPageNumber(pageCount);
    }
  }, [pageCount, pageNumber]);

  return {
    items,
    totalCount,
    pageNumber,
    pageCount,
    pagedItems,
    handlePageChange: setPageNumber,
    getPaginationAriaLabel,
  };
};

export default usePaginationV2;
